<template>
  <div class="home">
    <div v-if="hasPermission(PERMISSION_TYPE.DashboardStatistics)">
      <nav class="level mb-1">
        <div class="level-item has-text-centered">
          <div class="is-bordered">
            <p class="heading">類別首頁瀏覽量</p>
            <transition name="fade" mode="out-in">
              <p class="title" :key="catalogCount">
                {{ formatCount(catalogCount) }}
              </p>
            </transition>
          </div>
        </div>
        <div class="level-item has-text-centered">
          <div class="is-bordered">
            <p class="heading">線別首頁瀏覽量</p>
            <transition name="fade" mode="out-in">
              <p class="title" :key="areaCount">
                {{ formatCount(areaCount) }}
              </p>
            </transition>
          </div>
        </div>
        <div class="level-item has-text-centered">
          <div class="is-bordered">
            <p class="heading">網頁瀏覽量</p>
            <transition name="fade" mode="out-in">
              <p class="title" :key="totalCount">
                {{ formatCount(totalCount) }}
              </p>
            </transition>
          </div>
        </div>
        <div class="level-item has-text-centered">
          <div class="is-bordered">
            <p class="heading is-size-5 has-text-weight-bold">目前線上人數</p>
            <transition name="fade" mode="out-in">
              <p class="title is-size-2" :key="activeUsers">
                {{ activeUsers }}
              </p>
            </transition>
          </div>
        </div>
        <div class="level-item has-text-centered">
          <div class="is-bordered">
            <p class="heading">首頁瀏覽量</p>
            <transition name="fade" mode="out-in">
              <p class="title" :key="homeCount">
                {{ formatCount(homeCount) }}
              </p>
            </transition>
          </div>
        </div>
      </nav>

      <nav class="level mb-2">
        <div class="level-left">
          <div class="level-item">
            <b-field>
              <p class="control">
                <span class="button is-static">統計區間</span>
              </p>
              <b-datepicker
                v-model="selectedDates"
                range
                position="is-bottom-right"
                :placeholder="
                  $t('message.please_select') + $t('attributes.date_range')
                "
                @input="onSelectedDates"
                style="width: 210px;"
              >
                <button
                  class="button is-primary mx-1 my-1"
                  @click="setDates(1, 'week')"
                >
                  <span>一週</span>
                </button>
                <button
                  class="button is-success mx-1 my-1"
                  @click="setDates(1, 'month')"
                >
                  <span>一個月</span>
                </button>
                <button
                  class="button is-warning mx-1 my-1"
                  @click="setDates(3, 'month')"
                >
                  <span>三個月</span>
                </button>
                <button
                  class="button has-text-white has-background-success-dark mx-1 my-1"
                  @click="setDates(6, 'month')"
                >
                  <span>六個月</span>
                </button>
                <button
                  class="button has-text-white has-background-warning-dark mx-1 my-1"
                  @click="setDates(1, 'year')"
                >
                  <span>一年</span>
                </button>
              </b-datepicker>
            </b-field>
          </div>
        </div>
      </nav>

      <div class="columns is-desktop">
        <div class="column is-8-desktop">
          <div class="box" style="position: relative">
            <nav class="level mb-2">
              <div class="level-left">
                <div class="level-item">
                  <p class="title is-4 has-text-grey">網頁瀏覽量</p>
                </div>
              </div>
            </nav>
            <div style="width: 100%">
              <GChart
                ref="totalChart"
                type="AreaChart"
                :data="totalChartData"
                :options="totalOptions"
                style="width: 100%; height: 200px;"
              />
            </div>
            <b-loading
              :active="!totalReady"
              :is-full-page="false"
              :can-cancel="false"
            ></b-loading>
          </div>

          <div class="box" style="position: relative">
            <nav class="level mb-2">
              <div class="level-left">
                <div class="level-item">
                  <p class="title is-4 has-text-grey">首頁瀏覽量</p>
                </div>
              </div>
            </nav>
            <div style="width: 100%">
              <GChart
                ref="homeChart"
                type="AreaChart"
                :data="homeChartData"
                :options="homeOptions"
                style="width: 100%; height: 200px;"
              />
            </div>
            <b-loading
              :active="!homeReady"
              :is-full-page="false"
              :can-cancel="false"
            ></b-loading>
          </div>
        </div>

        <div class="column is-4-desktop">
          <div class="box has-text-centered">
            <p class="title is-4 has-text-grey">訂單、諮詢單</p>
            <p class="mb-4">已移至後台 2.0</p>
            <div class="buttons is-centered">
              <b-button
                type="is-primary"
                :loading="redirecting"
                @click="redirectToAdmin2('orders')"
              >
                前往後台 2.0 訂單
              </b-button>
              <b-button
                type="is-primary"
                :loading="redirecting"
                @click="redirectToAdmin2('inquiries')"
              >
                前往後台 2.0 諮詢單
              </b-button>
            </div>
          </div>
        </div>
      </div>

      <div class="columns is-desktop">
        <div class="column is-6-desktop">
          <div class="box" style="position: relative">
            <nav class="level mb-6">
              <div class="level-left">
                <div class="level-item">
                  <p class="title is-4 has-text-grey">線別首頁瀏覽量</p>
                </div>
              </div>
            </nav>
            <div style="width: 100%">
              <GChart
                type="ColumnChart"
                :data="areaChartData"
                :options="areaOptions"
                style="width: 100%; height: 280px;"
              />
            </div>
            <b-loading
              :active="!areaReady"
              :is-full-page="false"
              :can-cancel="false"
            ></b-loading>
          </div>
        </div>

        <div class="column is-6-desktop">
          <div class="box" style="position: relative">
            <nav class="level mb-1">
              <div class="level-left">
                <div class="level-item">
                  <p class="title is-4 has-text-grey">
                    類別首頁瀏覽量
                  </p>
                </div>
              </div>
            </nav>
            <nav class="level mb-2">
              <div class="level-left">
                <b-field>
                  <p class="control">
                    <span class="button is-static is-small">選擇線別</span>
                  </p>
                  <b-select
                    v-model="selectedArea"
                    size="is-small"
                    placeholder="選擇線別"
                    @input="onSelectedArea"
                  >
                    <option
                      v-for="option in managedAreaList"
                      :value="option"
                      :key="option.code"
                    >
                      {{ option.menu_name }}
                    </option>
                  </b-select>
                </b-field>
              </div>
            </nav>
            <div style="width: 100%">
              <GChart
                type="ColumnChart"
                :data="catalogChartData"
                :options="catalogOptions"
                style="width: 100%; height: 280px;"
              />
            </div>
            <b-loading
              :active="!catalogReady"
              :is-full-page="false"
              :can-cancel="false"
            ></b-loading>
          </div>
        </div>
      </div>

      <div class="columns is-desktop">
        <div class="column is-6-desktop">
          <div class="box" style="position: relative">
            <nav class="level mb-6">
              <div class="level-left">
                <div class="level-item">
                  <p class="title is-4 has-text-grey">團別首頁瀏覽量(前20)</p>
                </div>
              </div>
            </nav>
            <div style="width: 100%">
              <GChart
                type="ColumnChart"
                :data="productChartData"
                :options="productOptions"
                style="width: 100%; height: 280px;"
              />
            </div>
            <b-loading
              :active="!productReady"
              :is-full-page="false"
              :can-cancel="false"
            ></b-loading>
          </div>
        </div>

        <div class="column is-6-desktop">
          <div class="box" style="position: relative">
            <nav class="level mb-6">
              <div class="level-left">
                <div class="level-item">
                  <p class="title is-4 has-text-grey">旅行團首頁瀏覽量(前20)</p>
                </div>
              </div>
            </nav>
            <div style="width: 100%">
              <GChart
                type="ColumnChart"
                :data="groupChartData"
                :options="groupOptions"
                style="width: 100%; height: 280px;"
              />
            </div>
            <b-loading
              :active="!groupReady"
              :is-full-page="false"
              :can-cancel="false"
            ></b-loading>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="hasPermission(PERMISSION_TYPE.DashboardTourControl)">
      <tour-control-map />
    </div>
    <div v-else style="margin: 15% auto; text-align: center;">
      <p class="title is-4">會議室日程表已移至後台 2.0</p>
      <b-button
        type="is-primary"
        size="is-medium"
        :loading="redirecting"
        @click="redirectToAdmin2('meeting-room-schedule')"
      >
        前往後台 2.0 會議室日程表
      </b-button>
    </div>
  </div>
</template>

<script>
import { AREA_API, ANALYTICS_API } from '@/utils/apis'
import { mapGetters } from 'vuex'
import TourControlMap from '@/components/TourControlMap'
import notifyService from '@/services/notifyService'
import { openAdmin2 } from '@/utils/admin2'

export default {
  name: 'Home',
  components: { TourControlMap },

  data() {
    const { startDate, endDate } = this.getDateRange(1, 'week')

    return {
      timer: null,
      redirecting: false,

      selectedDates: [startDate, endDate],
      startDate,
      endDate,

      activeUsers: 0,

      loadingAreas: false,
      areaList: [],

      selectedArea: null,
      selectedAreaName: null,
      managedAreaList: [],

      profileId: 'ga:45482736',
      token: null,
      loading: false,
      loadingCharts: false,

      totalCount: null,
      totalReady: false,
      totalChartData: null,
      totalOptions: {
        title: null,
        pointSize: 6,
        colors: ['#0580b5', '#f4b40c'],
        areaOpacity: 0.1,
        chartArea: {
          left: 40,
          top: 20,
          bottom: 40,
          right: 40,
          width: '90%',
          height: '75%'
        },
        hAxis: {
          format: 'YYYY/MM/dd',
          gridlines: {
            color: 'none'
          }
        },
        vAxis: {
          minValue: 0
        },
        legend: { position: 'none' },
        animation: {
          startup: true,
          duration: 1000,
          easing: 'out'
        }
      },

      homeCount: null,
      homeReady: false,
      homeChartData: null,
      homeOptions: {
        title: null,
        pointSize: 6,
        colors: ['#0580b5', '#f4b40c'],
        areaOpacity: 0.1,
        chartArea: {
          left: 40,
          top: 20,
          bottom: 40,
          right: 40,
          width: '90%',
          height: '75%'
        },
        hAxis: {
          format: 'YYYY/MM/dd',
          gridlines: {
            color: 'none'
          }
        },
        vAxis: {
          minValue: 0
        },
        legend: { position: 'none' },
        animation: {
          startup: true,
          duration: 1000,
          easing: 'out'
        }
      },

      areaCount: null,
      areaReady: false,
      areaChartData: null,
      areaOptions: {
        title: null,
        colors: ['#0580b5', '#ffd713'],
        chartArea: {
          left: 40,
          top: 20,
          bottom: 80,
          right: 30,
          width: '90%',
          height: '75%'
        },
        hAxis: {
          slantedText: true,
          slantedTextAngle: 45 // here you can even use 180
        },
        vAxis: {
          minValue: 0
        },
        legend: { position: 'none' },
        tooltip: { isHtml: true },
        animation: {
          startup: true,
          duration: 1000,
          easing: 'out'
        }
      },

      catalogCount: null,
      catalogReady: false,
      catalogChartData: null,
      catalogOptions: {
        title: null,
        colors: ['#0580b5', '#ffd713'],
        chartArea: {
          left: 40,
          top: 20,
          bottom: 80,
          right: 30,
          width: '90%',
          height: '75%'
        },
        hAxis: {
          slantedText: true,
          slantedTextAngle: 45 // here you can even use 180
        },
        vAxis: {
          minValue: 0
        },
        legend: { position: 'none' },
        tooltip: { isHtml: true },
        animation: {
          startup: true,
          duration: 1000,
          easing: 'out'
        }
      },

      productCount: null,
      productReady: false,
      productChartData: null,
      productOptions: {
        title: null,
        colors: ['#0580b5', '#ffd713'],
        chartArea: {
          left: 40,
          top: 20,
          bottom: 80,
          right: 30,
          width: '90%',
          height: '75%'
        },
        hAxis: {
          slantedText: true,
          slantedTextAngle: 45 // here you can even use 180
        },
        vAxis: {
          minValue: 0
        },
        legend: { position: 'none' },
        tooltip: { isHtml: true },
        animation: {
          startup: true,
          duration: 1000,
          easing: 'out'
        }
      },

      groupCount: null,
      groupReady: false,
      groupChartData: null,
      groupOptions: {
        title: null,
        colors: ['#0580b5', '#ffd713'],
        chartArea: {
          left: 40,
          top: 20,
          bottom: 80,
          right: 30,
          width: '90%',
          height: '75%'
        },
        hAxis: {
          slantedText: true,
          slantedTextAngle: 45 // here you can even use 180
        },
        vAxis: {
          minValue: 0
        },
        legend: { position: 'none' },
        tooltip: { isHtml: true },
        animation: {
          startup: true,
          duration: 1000,
          easing: 'out'
        }
      }
    }
  },
  computed: {
    ...mapGetters('app', ['hasPermission']),
    isLoading() {
      return this.loading
    }
  },
  beforeDestroy() {
    clearInterval(this.timer)
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async redirectToAdmin2(page) {
      try {
        this.redirecting = true

        const url = await openAdmin2(this.axios, page)

        if (url) {
          window.location.href = url
        }
      } catch (e) {
        notifyService.fetch_error('jwt_token', e)
      } finally {
        this.redirecting = false
      }
    },
    async loadData() {
      this.loading = true
      await this.getAreaList()
      await this.getManagedAreaList()
      await this.initGoogleApi()
      this.loading = false
    },
    async getAreaList() {
      try {
        this.loadingAreas = true
        const res = await this.axios.get(`${AREA_API}/list`)
        const areas = res.data.data
        this.areaList = areas
          .sort(a => a.sort)
          .filter(a => !!a.menu_name)
          .map(area => {
            return {
              ...area,
              external_url:
                area.external_url !== null
                  ? area.external_url.replace(
                      'https://www.fantasy-tours.com',
                      ''
                    )
                  : ''
            }
          })
      } catch (e) {
        this.areaList = []

        notifyService.fetch_error('area', e)
      } finally {
        this.loadingAreas = false
      }
    },
    async getManagedAreaList() {
      try {
        this.loadingAreas = true
        const res = await this.axios.get(AREA_API)
        const areas = res.data.data
        this.managedAreaList = areas
          .filter(a => !!a.menu_name)
          .sort(a => a.sort)
          .map(area => {
            return {
              ...area,
              external_url:
                area.external_url !== null
                  ? area.external_url.replace(
                      'https://www.fantasy-tours.com',
                      ''
                    )
                  : ''
            }
          })
        this.selectedArea =
          this.managedAreaList.length > 0 ? this.managedAreaList[0] : null
        this.selectedAreaName = this.selectedArea && this.selectedArea.menu_name
      } catch (e) {
        this.managedAreaList = []
        this.selectedArea = null
        this.selectedAreaName = null

        notifyService.fetch_error('area', e)
      } finally {
        this.loadingAreas = false
      }
    },
    async initGoogleApi() {
      this.timer = setInterval(this.fetchActiveUsers, 10000)
      await this.fetchActiveUsers()
      await this.loadChartsData()
    },
    async loadChartsData() {
      this.loadingCharts = true
      await Promise.all([
        this.loadTotalChartData(),
        this.loadHomeChartData(),
        this.loadAreaChartData(),
        this.loadCatalogChartData(),
        this.loadProductChartData(),
        this.loadGroupChartData()
      ])
      this.loadingCharts = false
    },
    async reloadChartsData() {
      await Promise.all([
        this.loadTotalChartData(),
        this.loadHomeChartData(),
        this.loadAreaChartData(),
        this.loadCatalogChartData(),
        this.loadProductChartData(),
        this.loadGroupChartData()
      ])
    },
    async fetchActiveUsers() {
      const api = `${ANALYTICS_API}/active-users`
      const res = await this.axios.get(api)
      this.activeUsers = res.data.data
    },
    async loadTotalChartData() {
      try {
        this.totalReady = false
        const api = `${ANALYTICS_API}/total`
        const res = await this.axios.post(api, {
          start_date: this.$dayjs(this.startDate).format('YYYY-MM-DD'),
          end_date: this.$dayjs(this.endDate).format('YYYY-MM-DD')
        })

        const data = res.data.data
        const cols = data[0]
        const rows = data[1]
        this.totalChartData = [cols, ...rows]
        this.totalCount = rows.reduce(
          (total, row) => total + parseInt(row[1]),
          0
        )
      } catch (e) {
        this.totalChartData = null
        this.totalCount = 0

        // eslint-disable-next-line no-console
        console.log('loadTotalChartData', e)
      } finally {
        this.totalReady = true
      }
    },
    async loadHomeChartData() {
      try {
        this.homeReady = false
        const api = `${ANALYTICS_API}/home`
        const res = await this.axios.post(api, {
          start_date: this.$dayjs(this.startDate).format('YYYY-MM-DD'),
          end_date: this.$dayjs(this.endDate).format('YYYY-MM-DD')
        })

        const data = res.data.data
        const cols = data[0]
        const rows = data[1]
        this.homeChartData = [cols, ...rows]
        this.homeCount = rows.reduce(
          (total, row) => total + parseInt(row[1]),
          0
        )
      } catch (e) {
        this.homeChartData = null
        this.homeCount = 0

        // eslint-disable-next-line no-console
        console.log('loadHomeChartData', e)
      } finally {
        this.homeReady = true
      }
    },
    async loadAreaChartData() {
      try {
        this.areaReady = false
        const api = `${ANALYTICS_API}/area`
        const res = await this.axios.post(api, {
          start_date: this.$dayjs(this.startDate).format('YYYY-MM-DD'),
          end_date: this.$dayjs(this.endDate).format('YYYY-MM-DD')
        })

        const data = res.data.data
        let rows = data[1]

        const cols = []
        cols.push({ id: 'area', label: '線別', type: 'string' })
        cols.push({ id: 'value', label: '瀏覽量', type: 'number' })
        cols.push({ type: 'string', role: 'tooltip', p: { html: true } })

        const createTooltip = tip => {
          return `
            <div class="mx-1 my-1">
              <h1 class="has-text-black has-text-weight-bold mb-1" style="font-size: 1em; white-space: nowrap;">
                ${tip}
              </h1>
            </div>
          `
        }

        rows = rows.map(row => {
          return [row[0], row[1], createTooltip(row[2])]
        })

        this.areaChartData = [cols, ...rows]
        this.areaCount = rows.reduce(
          (total, row) => total + parseInt(row[1]),
          0
        )
      } catch (e) {
        this.areaChartData = null
        this.areaCount = 0

        // eslint-disable-next-line no-console
        console.log('loadAreaChartData', e)
      } finally {
        this.areaReady = true
      }
    },
    async loadCatalogChartData() {
      try {
        this.catalogReady = false

        const api = `${ANALYTICS_API}/catalog`
        const res = await this.axios.post(api, {
          area_code: this.selectedArea.code,
          start_date: this.$dayjs(this.startDate).format('YYYY-MM-DD'),
          end_date: this.$dayjs(this.endDate).format('YYYY-MM-DD')
        })

        const data = res.data.data
        let rows = data[1]

        const cols = []
        cols.push({ id: 'catalog', label: '類別', type: 'string' })
        cols.push({ id: 'value', label: '瀏覽量', type: 'number' })
        cols.push({ type: 'string', role: 'tooltip', p: { html: true } })

        const createTooltip = tip => {
          return `
            <div class="mx-1 my-1">
              <h1 class="has-text-black has-text-weight-bold mb-1" style="font-size: 1em; white-space: nowrap;">
                ${tip}
              </h1>
            </div>
          `
        }

        rows = rows.map(row => {
          return [row[0], row[1], createTooltip(row[2])]
        })

        this.catalogChartData = [cols, ...rows]
        this.catalogCount = rows.reduce(
          (total, row) => total + parseInt(row[1]),
          0
        )
      } catch (e) {
        this.catalogChartData = null
        this.catalogCount = 0

        // eslint-disable-next-line no-console
        console.log('loadCatalogChartData', e)
      } finally {
        this.catalogReady = true
      }
    },
    async loadProductChartData() {
      try {
        this.productReady = false
        const api = `${ANALYTICS_API}/product`
        const res = await this.axios.post(api, {
          start_date: this.$dayjs(this.startDate).format('YYYY-MM-DD'),
          end_date: this.$dayjs(this.endDate).format('YYYY-MM-DD')
        })

        const data = res.data.data
        let rows = data[1]

        const cols = []
        cols.push({ id: 'catalog', label: '團別', type: 'string' })
        cols.push({ id: 'value', label: '瀏覽量', type: 'number' })
        cols.push({ type: 'string', role: 'tooltip', p: { html: true } })

        const createTooltip = tip => {
          return `
            <div class="mx-1 my-1">
              <h1 class="has-text-black has-text-weight-bold mb-1" style="font-size: 1em; white-space: nowrap;">
                ${tip}
              </h1>
            </div>
          `
        }

        rows = rows.map(row => {
          return [row[0], row[1], createTooltip(row[2])]
        })

        this.productChartData = [cols, ...rows]
        this.productCount = rows.reduce(
          (total, row) => total + parseInt(row[1]),
          0
        )
      } catch (e) {
        this.productChartData = null
        this.productCount = 0

        // eslint-disable-next-line no-console
        console.log('loadProductChartData', e)
      } finally {
        this.productReady = true
      }
    },
    async loadGroupChartData() {
      try {
        this.groupReady = false
        const api = `${ANALYTICS_API}/group`
        const res = await this.axios.post(api, {
          start_date: this.$dayjs(this.startDate).format('YYYY-MM-DD'),
          end_date: this.$dayjs(this.endDate).format('YYYY-MM-DD')
        })

        const data = res.data.data
        let rows = data[1]

        const cols = []
        cols.push({ id: 'catalog', label: '個團', type: 'string' })
        cols.push({ id: 'value', label: '瀏覽量', type: 'number' })
        cols.push({ type: 'string', role: 'tooltip', p: { html: true } })

        const createTooltip = tip => {
          return `
            <div class="mx-1 my-1">
              <h1 class="has-text-black has-text-weight-bold mb-1" style="font-size: 1em; white-space: nowrap;">
                ${tip}
              </h1>
            </div>
          `
        }

        rows = rows.map(row => {
          return [row[0], row[1], createTooltip(row[2])]
        })

        this.groupChartData = [cols, ...rows]
        this.groupCount = rows.reduce(
          (total, row) => total + parseInt(row[1]),
          0
        )
      } catch (e) {
        this.groupChartData = null
        this.groupCount = 0

        // eslint-disable-next-line no-console
        console.log('loadGroupChartData', e)
      } finally {
        this.groupReady = true
      }
    },
    onSelectedDates() {
      this.startDate = this.selectedDates[0]
      this.endDate = this.selectedDates[1]

      this.reloadChartsData()
    },
    setDates(length, type) {
      const { startDate, endDate } = this.getDateRange(length, type)

      this.selectedDates = [startDate, endDate]
      this.startDate = startDate
      this.endDate = endDate

      this.reloadChartsData()
    },
    getDateRange(length, type) {
      const today = this.$dayjs()
      const startDate = today
        .add(1, 'day')
        .subtract(length, type)
        .toDate()
      const endDate = today.toDate()

      return { startDate, endDate }
    },
    onSelectedArea() {
      this.selectedAreaName = this.selectedArea && this.selectedArea.menu_name

      this.loadCatalogChartData()
    },
    formatCount(count) {
      return count !== null ? count : '-'
    }
  }
}
</script>

<style lang="scss" scoped>
.fade-enter-active {
  transition: all 0.3s ease;
}

.fade-leave-active {
  transition: all 0.8s cubic-bezier(1, 0.5, 0.8, 1);
}

.fade-enter-from,
.fade-leave-to {
  transform: translateY(-5px);
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}
</style>
